import { Anchor, Box, Divider, Flex, Group, Select, Text } from '@byecode/ui'
import type { StripePayConfig } from '@lighthouse/core'
import { FlowSelectSourceType } from '@lighthouse/core'
import { CURRENCY_OPTIONS, type FlowNode, getFieldOptions, getPrimaryDsSystemFieldId, getUpstreamRealDsId } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { SwitchControl } from '@/components/BlockSettings/Common/SwitchControl'
import { FieldSelect } from '@/components/FieldSelect'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { SettingRecordContent } from '@/containers/FlowDetail/components/SettingRecordContent'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import { EventSuccessAfter } from '../component/EventSuccessAfter'
import * as SC from './styles'

interface PayProps {
    allParentNodes?: FlowNode[]
    value: StripePayConfig
    onChange: (value: StripePayConfig) => void
}

const EmptyArray: FlowNode[] = []

const Pay: React.FunctionComponent<PayProps> = props => {
    const { allParentNodes = EmptyArray, value, onChange } = props
    const { type } = useFlowContainerContext()
    const isWorkflow = Boolean(type)
    const {
        nodeId,
        dataSourceId: dsId,
        selectType,
        descFieldId,
        productNameFieldId,
        amountFieldId,
        currency,
        succeedFieldSettings,
        productPictureFieldId,
        autoRedirectAfterPayment,
        callbackWorkflow
    } = value

    const dataSourceId = useMemo(() => {
        if (selectType === FlowSelectSourceType.UPSTREAM) {
            return getUpstreamRealDsId(nodeId, allParentNodes)
        }
        return dsId
    }, [allParentNodes, dsId, nodeId, selectType])

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, dataSourceId ?? '')
    const dataSourceList = useDataSourceList(appId, envId)

    const { schema, viewOptions, type: dsType } = dataSource || {}
    const { tableProps = [] } = viewOptions || {}
    const lookTextFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'TEXT')
    }, [schema, tableProps])

    const fileFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.type === 'file')
    }, [schema, tableProps])
    const lookNumberFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'NUMBER')
    }, [schema, tableProps])

    const primaryDsSystemFieldId = useMemo(() => {
        if (!schema || !viewOptions || !dsType) {
            return
        }
        return getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType)
    }, [schema, viewOptions, dsType])

    if (!dataSource) {
        return null
    }

    return (
        <>
            <Group label="支付订单信息">
                <SC.FieldList>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="orderId"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>订单号</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />

                        <Select
                            value={primaryDsSystemFieldId}
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            disabled
                        />
                    </SC.FieldItem>

                    <SC.FieldItem>
                        <PopoverQuestion
                            type="currency"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>货币类型</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />

                        <FieldSelect
                            options={CURRENCY_OPTIONS}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            value={currency}
                            onChange={currency => onChange({ ...value, currency })}
                        />
                    </SC.FieldItem>

                    <SC.FieldItem>
                        <Flex gap={16}>
                            <SC.FieldTitle required>订单金额</SC.FieldTitle>
                        </Flex>

                        <FieldSelect
                            options={lookNumberFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={amountFieldId}
                            onChange={amountFieldId => onChange({ ...value, amountFieldId })}
                        />
                        <Text size={12} lineHeight="18px" color="var(--color-gray-500)" style={{ marginTop: 4 }}>
                            相关货币的最大最小金额限制{' '}
                            <Anchor
                                target="_blank"
                                to="https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts"
                                style={{ color: 'var(--color-main)' }}
                            >
                                详见{'>'}
                            </Anchor>
                        </Text>
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="shopName"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>商品名称</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />

                        <FieldSelect
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={productNameFieldId}
                            onChange={productNameFieldId => onChange({ ...value, productNameFieldId })}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="shopDescribe"
                            renderTarget={(node, ref) => (
                                <Flex gap={4} ref={ref} alignItems="center">
                                    <SC.FieldTitle>商品描述</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <FieldSelect
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={descFieldId}
                            onChange={descFieldId => onChange({ ...value, descFieldId })}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="shopImg"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle>商品图</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />

                        <FieldSelect
                            options={fileFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={productPictureFieldId}
                            onChange={productPictureFieldId => onChange({ ...value, productPictureFieldId })}
                        />
                    </SC.FieldItem>
                </SC.FieldList>
            </Group>
            <Divider />
            <Group label="支付成功后更新订单">
                <Box px={8}>
                    <SettingRecordContent
                        dataSourceId={dataSource.id}
                        value={succeedFieldSettings}
                        onChange={succeedFieldSettings => onChange({ ...value, succeedFieldSettings })}
                        disablePageLink
                        disableFormOption
                        disableViewOption
                        disablePageOption
                    />
                </Box>
            </Group>
            <Divider />
            <EventSuccessAfter
                title="支付成功后更新订单"
                dataSource={dataSource}
                allParentNodes={allParentNodes}
                prefixNode={
                    <SwitchControl
                        label="跳转回原页面"
                        disabled={!isWorkflow}
                        checked={Boolean(autoRedirectAfterPayment)}
                        onChange={e => onChange({ ...value, autoRedirectAfterPayment: e.target.checked })}
                    />
                }
                value={callbackWorkflow || { workflowId: '', workflowArgs: [] }}
                onChange={callbackWorkflow => onChange({ ...value, callbackWorkflow })}
            />
        </>
    )
}

export default Pay
